import { supabase } from './supabase';

function buildMetadata(summaryData = {}) {
  const metadata = { ...(summaryData.metadata || {}) };

  metadata.settings = {
    method: summaryData.method ?? null,
    model: summaryData.model ?? null,
    temperature: summaryData.temperature ?? null,
    top_p: summaryData.topP ?? null,
    intended_audience: summaryData.intendedAudience ?? null,
    summary_style: summaryData.summaryStyle ?? null,
  };

  metadata.source_file = {
    name: summaryData.fileName ?? null,
    size: summaryData.fileSize ?? null,
    type: summaryData.fileType ?? null,
  };

  return metadata;
}

function normalizeSummaryRecord(record = {}) {
  const metadata = record.metadata || {};
  const settings = metadata.settings || {};
  const sourceFile = metadata.source_file || {};

  return {
    ...record,
    metadata,
    originalText: record.originalText ?? record.content ?? null,
    method: record.method ?? settings.method ?? null,
    model: record.model ?? settings.model ?? null,
    temperature: record.temperature ?? settings.temperature ?? null,
    topP: record.topP ?? record.top_p ?? settings.top_p ?? null,
    intendedAudience: record.intendedAudience ?? settings.intended_audience ?? null,
    summaryStyle: record.summaryStyle ?? settings.summary_style ?? null,
    fileName: record.fileName ?? sourceFile.name ?? null,
    fileSize: record.fileSize ?? sourceFile.size ?? null,
    fileType: record.fileType ?? sourceFile.type ?? null,
  };
}

export const summaryStorage = {
  // Save a new summary to Supabase
  saveSummary: async (summaryData) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.warn('Cannot save summary: User not authenticated');
        return null;
      }

      // Prepare data for insertion
      const newSummary = {
        user_id: user.id,
        content: summaryData.text || summaryData.content || summaryData.originalText, // Handle different property names
        summary: summaryData.summary,
        metadata: buildMetadata(summaryData),
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('summaries')
        .insert([newSummary])
        .select()
        .single();

      if (error) throw error;

      return normalizeSummaryRecord(data);
    } catch (error) {
      console.error('Failed to save summary to Supabase:', error);
      return null;
    }
  },

  // Get all summaries from Supabase for current user
  getSummaries: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];

      const { data, error } = await supabase
        .from('summaries')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []).map(normalizeSummaryRecord);
    } catch (error) {
      console.error('Failed to retrieve summaries from Supabase:', error);
      return [];
    }
  },

  // Delete a specific summary by ID
  deleteSummary: async (id) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('summaries')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to delete summary:', error);
      return false;
    }
  },

  // Clear all summaries
  clearAll: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { error } = await supabase
        .from('summaries')
        .delete()
        .eq('user_id', user.id); // RLS should handle this, but being explicit doesn't hurt

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Failed to clear summary history:', error);
      return false;
    }
  },

  // Get summary count
  getCount: async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;

      // Use count exact for efficiency
      const { count, error } = await supabase
        .from('summaries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      if (error) throw error;
      return count;
    } catch (error) {
      console.error('Failed to get summary count:', error);
      return 0;
    }
  }
};
